'use client';

type LoyaltyUser = {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  telephone: string;
  gender: 'Male' | 'Female' | string;
  date_of_birth: string;
  points: number;
  amountSpent: number;
  tier: 'Ruby' | 'Bronze' | 'Silver' | 'Gold' | 'Diamond' | 'Platinum' | string;
  reachedMilestone: boolean;
  redeemedTiers: string[];
};

type Reward = {
  _id: string;
  name: string;
  tier: 'Ruby' | 'Bronze' | 'Silver' | 'Gold' | 'Diamond' | 'Platinum' | string;
  description: string;
  pointsRequired: number;
  imageUrl: string;
};

type Redemption = {
  _id: string;
  l_user_id: string;
  tier: 'Ruby' | 'Bronze' | 'Silver' | 'Gold' | 'Diamond' | 'Platinum' | string;
  reward_id: string;
  outletId: string;
  pickupDate: string | null;
  status: 'pending' | 'completed' | 'cancelled' | 'failed';
  route: string;
};

type CartData = {
  _id: string;
  name: string;
  tier: string;
  description: string;
  pointsRequired: number;
  imageUrl: string;
  menuId: string;
  price: number;
};

interface CartReward {
  id: string;
  quantity: number;
  isCombo: boolean;
  price: number;
  cartData: CartData;
}
type PointsHistory = {
  _id: string;
  orderNo: string;
  status: string;
  pointsAwardedAt: Date;
  points: number;
};
type RedeemHistory = {
  _id: string;
  redeemedAt: string;
  pointsRequired: string;
  reward_id: {
    _id: string;
    pointsRequired: number;
    tier: string;
  };
};

type ManualHistory = {
  _id: string;
  points: number;
  orderNo: string;
  adminId: {
    first_name: string;
    role: string;
    outlet_id: {
      orderCode: string;
    };
  };
  assignedAt: Date;
};

type Props = {
  loyaltyUser: LoyaltyUser | null;
  rewards: Reward[];
  requiredPoints: number | null;
  pointTier:
    | 'Ruby'
    | 'Bronze'
    | 'Silver'
    | 'Gold'
    | 'Diamond'
    | 'Platinum'
    | string
    | null;
};

import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import f_avatar from '@/assets/f_avatar.png';
import progress_avatar from '@/assets/progress_avatar.png';
import loyaltyGreen from '@/assets/loyalty_green.png';
import loyaltyGray from '@/assets/loyalty_gray.png';
import loyaltyWhite from '@/assets/loyalty_white.png';
import snacks from '@/assets/snacks.png';
import unlock from '@/assets/unlock.png';
import info from '@/assets/loyalty_info.png';
import axiosInstance from '@/utils/https';
import { useDispatch, useSelector } from 'react-redux';
import { addToCart, removeFromCart } from '@/redux/slices/cart.slice';
import AppStorage, { getErrorMessge } from '@/utils/helpers';
import { toast } from 'react-toastify';
import Modal from '../global/Modal';
import outlet from '@/assets/outlet.png';
import outlet_white from '@/assets/outlet_white.png';
import shoppingBag from '@/assets/shopping-bag.png';
import shoppingBagWhite from '@/assets/shopping-bag_white.png';
import { CheckIcon } from '@heroicons/react/24/solid';
import loyalty from '@/assets/loyalty_spark.png';
import mark from '@/assets/mark.png';
import ranking from '@/assets/ranking.png';
import History from './History';
import Calendar from '../Calendar';
import { format } from 'date-fns';
import LocationDetails from '../global/LocationDetails';
import LocationWarning from '../global/LocationWarning';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

function View({ loyaltyUser, rewards, requiredPoints, pointTier }: Props) {
  const { cart } = useSelector((state: any) => state.cart);
  const dispatch = useDispatch();
  const [isHistory, setIsHistory] = useState<boolean>(false);
  const [redemption, setRedemption] = useState<Redemption | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [openRedeemModal, setOpenRedeemModal] = useState<boolean>(false);
  const [openOutletModal, setOpenOutletModal] = useState<boolean>(false);
  const [openConfirmReward, setOpenConfirmReward] = useState<boolean>(false);
  const [outletCode, setOutletCode] = useState<string | null>('');
  const [outletName, setOutletName] = useState<string | null>(null);
  const [outletId, setOutletId] = useState<string | null>(null);
  const [pickupDate, setPickupDate] = useState<string | null>(null);
  const [openDateModal, setOpenDateModal] = useState<boolean>(false);
  const [hasTyped, setHasTyped] = useState<boolean>(false);
  const [isRedeemLoading, setIsRedeemLoading] = useState(false);
  const [isOutletLoading, setIsOutletLoading] = useState(false);
  const [isInAppLoading, setIsInAppLoading] = useState(false);
  const [redeemed, setRedeemed] = useState(false);
  const [pointsHistory, setPointsHistory] = useState<PointsHistory[] | []>([]);
  const [redeemHistory, setRedeemHistory] = useState<RedeemHistory[] | []>([]);
  const [manualHistory, setManualHistory] = useState<ManualHistory[] | []>([]);

  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [branch, setBranch] = useState('');
  const [branches, setBranches] = useState<
    {
      _id: string;
      name: string;
      city: string;
      state: string;
      location: { coordinates: [number, number] };
    }[]
  >([]);
  const [showWarning, setShowWarning] = useState<boolean>(false);

  const handleSelect = (method: string) => {
    setSelected(method);
    AppStorage.save('redeem_route', method);
  };

  useEffect(() => {
    fetchUserRedemptionTier();
    fetchPointsHistory();
    fetchRedeemHistory();
    fetchManualPointHistory();
  }, []);

  const fetchPointsHistory = async () => {
    try {
      const res =
        await axiosInstance.get<PointsHistory[]>(`/api/v1/cart/history`);
      setPointsHistory(res.data);
    } catch (error) {
      console.log('Error fetching Loyalty Points History: ', error);
    }
  };

  const fetchManualPointHistory = async () => {
    try {
      const res = await axiosInstance.get<ManualHistory[]>(
        `/api/v1/loyalty/history/${loyaltyUser?._id}`
      );
      setManualHistory(res.data);
    } catch (error) {
      console.log('Error fetching Loyalty Points History: ', error);
    }
  };

  const fetchRedeemHistory = async () => {
    try {
      const res = await axiosInstance.get(
        `api/v1/redeem/history/${loyaltyUser?._id}`
      );
      setRedeemHistory(res.data);
    } catch (error) {
      console.log('Error retrieving redemption history: ', error);
    }
  };

  const fetchUserRedemptionTier = async () => {
    try {
      const res = await axiosInstance.get(
        `api/v1/redeem/${loyaltyUser?._id.toString()}/${pointTier ?? tier}`
      );
      setRedemption(res.data);

      if (
        res.data &&
        (res.data.route === 'outlet' ||
          res.data.status === 'ACCEPTED' ||
          res.data.status === 'FULFILLED')
      ) {
        setRedeemed(true);
      }
    } catch (error) {
      console.log('Error fetching redemption:', error);
    }
  };

  // const fetchOutletByCode = async (outletCode: string) => {
  //   try {
  //     const res = await axiosInstance.get(
  //       `api/v1/outlets/${outletCode.toUpperCase()}`
  //     );
  //     setOutletName(res.data[0]?.name ?? null);
  //     setOutletId(res.data[0]?._id);
  //   } catch (error) {
  //     console.log(error);
  //   }
  // };

  const fetchRewardByTier = async (tier: string) => {
    try {
      const route = AppStorage.get('redeem_route');
      const res = await axiosInstance.get(
        `api/v1/rewards/tier?tier=${pointTier ?? tier}`
      );

      if (route === 'inApp') {
        const err = await handleConfirmInApp();
        if (err) {
          toast.error(err);
          return;
        }
        fetchUserRedemptionTier();
        const check = await addItemToCart(res.data);
        if (check) {
          return check;
        }
      }
      if (route !== 'inApp') {
        return res.data;
      }
    } catch (error) {
      console.error('Error fetching rewards by tier:', error);
    }
  };
  const { firstName, tier, points } = loyaltyUser || {};

  async function addItemToCart(reward: CartReward) {
    try {
      if (cart && cart.length > 0) {
        const checkCartForReward = cart.find(
          (item: any) => item.id === reward.id
        );
        if (checkCartForReward) {
          toast.error('Reward already in your cart');
          setOpenRedeemModal(false);
          return checkCartForReward;
        }
      }
      dispatch(addToCart(reward));
    } catch (e) {
      console.log('===> issue with adding item to cart', e);
    }
  }

  const redeemable = requiredPoints != null && (points ?? 0) >= requiredPoints;

  const handleConfirmRedeem = async () => {
    const route = AppStorage.get('redeem_route');

    if (route === 'inApp') {
      setIsRedeemLoading(true);

      const check = await fetchRewardByTier(pointTier ?? tier ?? '');
      setIsRedeemLoading(false);

      if (check) {
        setOpenRedeemModal(false);
        // setOpenConfirmReward(true);
      } else {
        setOpenRedeemModal(false);
        setOpenConfirmReward(true);
      }
    } else {
      setOpenRedeemModal(false);
      setOpenOutletModal(true);
    }
  };

  const handleConfirmOutlet = async () => {
    try {
      setIsOutletLoading(true);

      const url = `/api/v1/redeem`;
      const res = await axiosInstance.post(url, {
        l_user_id: loyaltyUser?._id,
        inApp: selected === 'inApp',
        tier: pointTier,
        outletId: selected === 'inApp' ? null : outletId,
        pickupDate,
      });

      toast.success(res.data.message);

      if (res.data.notification) {
        const tierData = await fetchRewardByTier(pointTier ?? tier ?? '');
        dispatch(removeFromCart(tierData.id));
      }

      setOpenDateModal(false);
      setOpenConfirmReward(true);
      fetchUserRedemptionTier();
    } catch (error) {
      console.error('Error redeeming points:', error);
      const err = getErrorMessge(error);
      toast.error(err);
    } finally {
      setIsOutletLoading(false);
    }
  };

  const handleConfirmInApp = async () => {
    try {
      setIsInAppLoading(true);

      const url = `/api/v1/redeem`;
      const res = await axiosInstance.post(url, {
        l_user_id: loyaltyUser?._id,
        inApp: selected === 'inApp',
        tier: pointTier,
        outletId: selected === 'inApp' ? null : outletId,
      });

      toast.success(res.data.message);
      AppStorage.save('loyaltyUser', JSON.stringify(loyaltyUser));
      AppStorage.save('pointTier', pointTier);
    } catch (error) {
      console.error('Error redeeming points:', error);
      const err = getErrorMessge(error);
      return err;
    } finally {
      setIsInAppLoading(false);
    }
  };

  // const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (!hasTyped) setHasTyped(true);
  //   setOutletCode(e.target.value);
  // };

  const handleOutletModal = () => {
    setOpenOutletModal(false);
    setOutletName(null);
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      const formattedDate = format(date, 'MMM dd, yyyy h:mm aa');
      setPickupDate(formattedDate);
    }
  };

  const currentReward = rewards.find((reward) => reward.tier === tier);
  const handleLocation = () => {
    const selectedBranch = branches.find(
      (branchObj: any) => branchObj.name === branch
    );
    setOutletName(selectedBranch?.name ?? '');
    setOutletId(selectedBranch?._id ?? '');
  };

  return (
    <div className="w-full px-4 py-8 sm:p-8">
      <header>
        {isHistory && (
          <div
            onClick={() => setIsHistory(false)}
            className="w-max mb-6 flex items-center gap-2 cursor-pointer hover:text-crunchies-red"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span className="text-xl font-semibold text-[#100A37] hover:text-crunchies-red">
              Dashboard
            </span>
          </div>
        )}

        {!isHistory && (
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">My Loyalty Program</h2>
            <p
              onClick={() => setIsHistory(true)}
              className="text-red-500 text-sm underline cursor-pointer"
            >
              History
            </p>
          </div>
        )}

        <div className="flex items-center gap-5 mb-4">
          <Image src={f_avatar} alt="User" className="w-12 h-12 rounded-full" />
          <div>
            <h3 className="font-semibold">{firstName}</h3>
            <div className="flex items-center gap-2">
              <Image src={ranking} alt="Ranking" />
              <p className="text-sm font-medium text-[#475569]">
                {tier} Cruncher
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <div className="flex items-center gap-2  font-semibold">
            <Image src={loyaltyGreen} alt="Loyalty Icon" />
            <span>
              {points?.toFixed(1)}/{currentReward?.pointsRequired} points
            </span>
          </div>
          <button
            disabled={redeemed || !redeemable}
            onClick={() => setOpenRedeemModal(true)}
            type="button"
            className={`text-gray-400 px-4 py-2 rounded-full text-sm flex items-center gap-2 ${redeemed || !redeemable ? 'bg-gray-100 cursor-not-allowed' : 'bg-crunchies-red text-white'}`}
          >
            {redeemed || !redeemable ? (
              <Image src={loyaltyGray} alt="Loyalty Gray Icon" />
            ) : (
              <Image src={loyaltyWhite} alt="Loyalty White Icon" />
            )}
            <span>Redeem point</span>
          </button>
        </div>
      </header>

      <Modal
        openModal={openRedeemModal}
        closeModal={() => setOpenRedeemModal(false)}
        modalTitle="Redeem Reward"
        closeButton={true}
        shouldCloseOnOverlayClick={false}
        darkTitle={true}
      >
        <div className="flex flex-col gap-4 mb-4">
          <p className="text-[#475569]">
            Select how you will like to redeem your reward
          </p>

          <div className="space-y-4 mt-6">
            <div
              className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer ${
                selected === 'outlet'
                  ? 'border-crunchies-red'
                  : 'border-gray-200'
              }`}
              onClick={() => handleSelect('outlet')}
            >
              <div
                className={`p-2 rounded-full ${selected === 'outlet' ? 'bg-crunchies-red' : 'bg-gray-100'}`}
              >
                {selected === 'outlet' ? (
                  <Image src={outlet_white} alt="outlet bag" />
                ) : (
                  <Image src={outlet} alt="outlet bag" />
                )}
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-sm text-[#0F172A]">
                  In outlet
                </h4>
                <p className="text-xs text-[#475569]">
                  Your reward will be given to you at a chosen outlet
                </p>
              </div>
              <div className="mt-1">
                <div
                  className={`p-2 border rounded-full  ${selected === 'outlet' ? 'bg-crunchies-red border-crunchies-red' : 'bg-white border-gray-300'}`}
                >
                  <CheckIcon className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>

            <div
              className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer ${
                selected === 'inApp'
                  ? 'border-crunchies-red'
                  : 'border-gray-200'
              }`}
              onClick={() => handleSelect('inApp')}
            >
              <div
                className={`p-2 rounded-full ${selected === 'inApp' ? 'bg-crunchies-red' : 'bg-gray-100'}`}
              >
                {selected === 'inApp' ? (
                  <Image src={shoppingBagWhite} alt="Shopping bag" />
                ) : (
                  <Image src={shoppingBag} alt="Shopping bag" />
                )}
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-sm text-[#0F172A]">
                  Online / In-App
                </h4>
                <p className="text-xs text-[#475569]">
                  Your reward will be added to your cart and you can check out
                  whenever you want
                </p>
              </div>
              <div className="mt-1">
                <div
                  className={`p-2 rounded-full border  ${selected === 'inApp' ? 'bg-crunchies-red border-crunchies-red' : 'bg-white border-gray-300'}`}
                >
                  <CheckIcon className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <button
          disabled={!selected}
          className={`text-white bg-crunchies-red py-3 px-6 rounded-[14px] font-semibold text-center ${
            selected && !isRedeemLoading
              ? 'bg-crunchies-red hover:bg-crunchies-red/80'
              : 'bg-[#B0B0B033] text-[#2b2a2a] cursor-not-allowed'
          }`}
          onClick={handleConfirmRedeem}
        >
          {isRedeemLoading ? 'Confirming...' : 'Continue'}
        </button>
      </Modal>

      <Modal
        modalTitle="Choose outlet"
        openModal={openOutletModal}
        closeModal={handleOutletModal}
        closeButton={true}
        shouldCloseOnOverlayClick={false}
      >
        <div className="flex justify-center mb-4">
          <div className="bg-[#FFE4E8] p-4 rounded-full">
            <Image src={loyalty} alt="Loyalty Spark" priority />
          </div>
        </div>
        <LocationDetails
          setState={setState}
          state={state}
          setCity={setCity}
          city={city}
          setBranch={setBranch}
          branch={branch}
          setBranches={setBranches}
          setShowWarning={setShowWarning}
        />

        <button
          disabled={branch.length === 0}
          className={`text-white px-6 py-3 w-full text-center mx-auto rounded-[14px] ${
            branch.length === 0
              ? 'bg-[#B0B0B033] text-[#181818] cursor-not-allowed'
              : 'bg-crunchies-red hover:bg-crunchies-red/80'
          }`}
          onClick={() => {
            handleLocation();
            setOpenOutletModal(false);
            setOpenDateModal(true);
          }}
        >
          Continue
        </button>
      </Modal>

      <Modal
        openModal={showWarning}
        closeModal={() => setShowWarning(false)}
        modalTitle=""
        closeButton={true}
        shouldCloseOnOverlayClick={true}
      >
        <LocationWarning />
      </Modal>

      <Modal
        openModal={openDateModal}
        closeModal={() => setOpenDateModal(false)}
        modalTitle=""
        closeButton={true}
        shouldCloseOnOverlayClick={false}
      >
        <div className="flex justify-center mb-4">
          <div className="bg-[#FFE4E8] p-4 rounded-full">
            <Image src={loyalty} alt="Loyalty Spark" priority />
          </div>
        </div>

        {/* Title and Description */}
        <h2 className="text-center text-2xl font-semibold text-[#100A37]">
          Pick Up Date
        </h2>
        <p className="text-center text-sm text-[#475569] mt-1 mb-6">
          Please select a Date and Time to pickup your reward at our Store
        </p>

        <div className="mb-6">
          <label
            htmlFor="outlet-code"
            className="block text-sm font-medium text-[#0F172A] mb-1"
          >
            Pickup Date
          </label>
          <Calendar
            selectedDate={pickupDate ? new Date(pickupDate) : null}
            onChange={handleDateChange}
            dateFormat="MMMM dd, yyyy h:mm aa"
            showTimeSelect={true}
          />
        </div>

        <button
          disabled={!pickupDate}
          className={`text-white py-3 px-6 rounded-[14px] font-semibold text-center ${
            pickupDate !== null && !isOutletLoading
              ? 'bg-crunchies-red hover:bg-crunchies-red/80'
              : 'bg-[#B0B0B033] text-[#181818] cursor-not-allowed'
          }`}
          onClick={handleConfirmOutlet}
        >
          {isOutletLoading ? 'Confirming...' : 'Continue'}
        </button>
      </Modal>

      <Modal
        openModal={openConfirmReward}
        closeModal={() => setOpenConfirmReward(false)}
        modalTitle=""
        closeButton={true}
        shouldCloseOnOverlayClick={false}
      >
        <div className="flex flex-col items-center justify-center gap-10">
          <div className="w-32 h-32 flex items-center justify-center rounded-full border-[3px] border-green-600 transition-opacity">
            <Image src={mark} alt="mark" priority />
          </div>

          <h2 className="text-lg font-bold text-gray-900 mb-2">
            {selected === 'inApp' ? 'Added to Cart' : 'Sent to outlet'}
          </h2>

          {selected === 'inApp' ? (
            <p className="text-sm text-gray-600 text-center">
              Your reward has been added to your cart, go to cart and check out
              to receive your reward.
            </p>
          ) : (
            <p className="text-sm text-gray-600 text-center mb-10">
              Your reward has been sent to <br />
              <span className="font-semibold text-black">{outletName}</span>.
              <br />
              Kindly meet with the sales person at the outlet to get your
              reward.
            </p>
          )}

          {selected === 'inApp' && (
            <a href="#" className="text-crunchies-red underline">
              see your reward in cart
            </a>
          )}
        </div>
      </Modal>

      {!isHistory && (
        <main>
          <div className="flex flex-col gap-5">
            {rewards.map((reward, index, arr) => (
              <div
                key={index}
                className={`rounded-xl p-4 ${
                  tier === reward.tier ? 'bg-red-100' : 'bg-[#F8F8F8]'
                }`}
              >
                <div className="w-full flex items-center gap-4">
                  {tier !== reward.tier &&
                    (points ?? 0) < reward.pointsRequired && (
                      <Image src={unlock} alt="unlock" priority />
                    )}
                  <div className="w-full">
                    <div className="w-full flex items-start gap-4 mb-2">
                      {tier === reward.tier &&
                        (points ?? 0) <= reward.pointsRequired && (
                          <Image src={progress_avatar} alt="Avatar" priority />
                        )}
                      {tier !== reward.tier &&
                        (points ?? 0) > reward.pointsRequired && (
                          <Image src={f_avatar} alt="Avatar" priority />
                        )}
                      <div className="w-full">
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-semibold">
                            {reward.tier} Cruncher
                          </h4>
                          <Image src={info} alt="Loyalty Info" priority />
                        </div>

                        {tier === reward.tier && (
                          <p className="text-sm text-gray-600 mb-2">
                            {reward.description}
                          </p>
                        )}
                        <div className="w-full flex justify-between items-center gap-3">
                          <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden mb-1">
                            <div
                              className={`h-full ${
                                (points ?? 0) >= reward.pointsRequired ||
                                tier === reward.tier
                                  ? 'bg-crunchies-red'
                                  : 'bg-pink-200'
                              }`}
                              style={{
                                width: `${
                                  (points ?? 0) >= reward.pointsRequired
                                    ? 100
                                    : (((points ?? 0) -
                                        (arr[index - 1]?.pointsRequired ?? 0)) /
                                        (reward.pointsRequired -
                                          (arr[index - 1]?.pointsRequired ??
                                            0))) *
                                      100
                                }%`,
                              }}
                            ></div>
                          </div>
                          <Image src={snacks} alt="Snacks" />
                        </div>
                        <p className="text-xs text-gray-500">
                          {(points ?? 0) < reward.pointsRequired
                            ? points?.toFixed(1)
                            : reward.pointsRequired}
                          /{reward.pointsRequired} points
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </main>
      )}

      {isHistory && (
        <History
          pointsHistory={pointsHistory}
          redeemHistory={redeemHistory}
          manualHistory={manualHistory}
          activeReward={currentReward}
          points={points}
          tier={tier}
        />
      )}
    </div>
  );
}

export default View;
